"use client";

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ClientReportCard } from "@/app/(app)/relatorios/components/client-report-card";
import { VehicleDetailsDialog } from "@/components/vehicles/vehicle-details-dialog";
import { WorkOrderDetailsDialog } from "@/components/work-orders/work-order-details-dialog";
import { AppDialog } from "@/components/ui/app-dialog";
import { apiClient } from "@/lib/api-client";
import type { ClientReport } from "@/types/reports";

type ClientDetailsDialogProps = {
  clientId: string | null;
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  backLabel?: string;
};

export function ClientDetailsDialog({
  clientId,
  open,
  onClose,
  onBack,
  backLabel = "Voltar",
}: ClientDetailsDialogProps) {
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(null);
  const clientQuery = useQuery({
    queryKey: ["reports", "clients", clientId],
    enabled: open && Boolean(clientId),
    queryFn: async () => {
      const response = await apiClient.get<ClientReport>(`/reports/clients/${clientId}`);
      return response.data;
    },
  });

  const client = clientQuery.data;

  const handleClose = () => {
    setSelectedVehicleId(null);
    setSelectedWorkOrderId(null);
    onClose();
  };

  if (selectedWorkOrderId) {
    return (
      <WorkOrderDetailsDialog
        open={open}
        workOrderId={selectedWorkOrderId}
        onClose={handleClose}
        onBack={() => setSelectedWorkOrderId(null)}
        onOpenClient={() => setSelectedWorkOrderId(null)}
        onOpenVehicle={(vehicleId) => {
          setSelectedWorkOrderId(null);
          setSelectedVehicleId(vehicleId);
        }}
      />
    );
  }

  if (selectedVehicleId) {
    return (
      <VehicleDetailsDialog
        open={open}
        vehicleId={selectedVehicleId}
        onClose={handleClose}
        onBack={() => setSelectedVehicleId(null)}
        onOpenClient={() => setSelectedVehicleId(null)}
        onOpenWorkOrder={setSelectedWorkOrderId}
      />
    );
  }

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title={client ? client.name : "Detalhes do cliente"}
      description="Dados cadastrais, veículos, histórico e visão financeira"
      size="xl"
      mobileFullscreen
      actions={
        <>
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              {backLabel}
            </button>
          ) : null}
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
          >
            Fechar
          </button>
        </>
      }
    >
      {clientQuery.isLoading ? (
        <div aria-live="polite" aria-busy="true" className="py-12 text-center text-sm text-slate-500">
          Carregando dados do cliente...
        </div>
      ) : clientQuery.isError ? (
        <div aria-live="assertive" className="space-y-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>Não foi possível carregar os dados deste cliente.</p>
          <button
            type="button"
            onClick={() => clientQuery.refetch()}
            className="font-semibold underline underline-offset-2"
          >
            Tentar novamente
          </button>
        </div>
      ) : client ? (
        <ClientReportCard
          report={client}
          startOpen
          showDetailsLink={false}
          onOpenVehicle={setSelectedVehicleId}
          onOpenWorkOrder={setSelectedWorkOrderId}
        />
      ) : null}
    </AppDialog>
  );
}
